import { Entity } from '@square/ignition';
import ArrowLeft from '@squareup/dex-icons/market/arrow/Left';
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { NavigationItem, NavigationList, Link } from '@squareup/dex-ui';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Box, Heading20 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import { MarketButton } from '@squareup/dex-ui-shared-market';
import { NavigationMobileToggleProps } from '@squareup/dex-ui-shared-mobile-nav';
import {
  areEntityListsEqual,
  compareGraphObject,
} from '@squareup/dex-utils-oas';
import { breakHtmlForCamelCaseReact } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { useCallback, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import { ReferenceStickyNavigation } from '../ReferenceStickyNavigation';

import styles from './reference-object-navigation.module.css';

interface ReferenceObjectNavigationProps {
  entities: Entity[] | undefined;
  isObjectNav: boolean;
  activeVersion: string;
  activeItem?: Entity | undefined;
}

const ReferenceObjectNavigation = React.memo(
  ({
    entities,
    isObjectNav,
    className,
    activeVersion,
    activeItem,
    isMobileNavOpen,
  }: ReferenceObjectNavigationProps &
    NullableClassName &
    NavigationMobileToggleProps) => {
    const hrefBackButtonBuilder = isObjectNav
      ? ReferenceHrefBuilders.versionObjects
      : ReferenceHrefBuilders.versionEnums;

    const hrefItemBuilder = useCallback(
      (entity: Entity) => {
        if (entity.isEnum) {
          return ReferenceHrefBuilders.versionEnum({
            square_version: activeVersion,
            enum: entity.name,
          });
        } else {
          return ReferenceHrefBuilders.versionObject({
            square_version: activeVersion,
            object: entity.name,
          });
        }
      },
      [activeVersion]
    );

    const ref = useRef<globalThis.HTMLMarketButtonElement>(null);
    const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
      ref,
      'market-button'
    );

    return (
      <ReferenceStickyNavigation
        testId="reference-object-navigation"
        className={clsx(styles.nav, className)}
        isMobileNavOpen={isMobileNavOpen}
      >
        <Box margin={{ left: '1x' }}>
          <Link
            trackingId="navigate-to-reference-object-back"
            omitFocus={true}
            className={styles['back-button-link']}
            href={hrefBackButtonBuilder({
              square_version: activeVersion,
            })}
          >
            <MarketButton
              aria-label="Back"
              trackingId="navigate-to-reference-object-back"
              rank="secondary"
              size="small"
              className={clsx(
                styles['back-button'],
                !isMarketComponentLoaded && styles.hidden
              )}
              ref={onMarketRef}
            >
              <ArrowLeft
                slot="icon"
                className={commonIconStyles['icon-color']}
              ></ArrowLeft>
            </MarketButton>
          </Link>
          <Heading20 margin={{ top: '2x', bottom: '1x' }}>
            {isObjectNav ? 'Objects' : 'Enums'}
          </Heading20>
        </Box>

        <NavigationList isSubNavigation={true} enableOverflowShimmer={true}>
          {entities?.sort(compareGraphObject).map((entity) => {
            const { name, id } = entity;
            return (
              <NavigationItem
                trackingId={`navigate-to-reference-object-${name}`}
                isSubItem={true}
                key={id}
                href={hrefItemBuilder(entity)}
                testId={id}
                // TODO: Look into this, html-react-parser is really slow
                text={breakHtmlForCamelCaseReact(name)}
                isActive={activeItem?.id === id}
              ></NavigationItem>
            );
          })}
        </NavigationList>
      </ReferenceStickyNavigation>
    );
  },
  (prevProps, nextProps) => {
    const { entities: prevPropEntities, ...prevPropsRest } = prevProps;
    const { entities: nextPropsEntities, ...nextPropsRest } = nextProps;

    if (!shallowEqual(prevPropsRest, nextPropsRest)) {
      return false;
    }

    // Entities aren't stable, but their value doesn't actually change.
    // So doing an equality check on them otherwise the rerender cost is high
    return areEntityListsEqual(prevPropEntities, nextPropsEntities);
  }
);

ReferenceObjectNavigation.displayName = 'ReferenceObjectNavigation';

export { ReferenceObjectNavigation };
